import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { MonthPaymentDetail } from "../../../../interfaces/earnings";
import { useHttpClient } from "../../../../hooks/useHttpClient";
import { useLocalization } from "../../../../stores/localizationContext";
import { useUserContext } from "../../../../stores/userContext";
import AdminTable, { type AdminTableColumn } from "../../Common/AdminTable/AdminTable";
import {
  deriveScheduleString,
  formatPaymentDate,
  formatPaymentType,
} from "../../../../utils/earningsUtils";
import "./MonthEarnings.scss";

const DASH = "—";

const MonthEarnings: React.FC = () => {
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t, lang } = useLocalization();
  const navigate = useNavigate();
  const { language, year, month } = useParams<{
    language: string;
    year: string;
    month: string;
  }>();

  const locale = lang === "ar" ? "ar-EG" : "en-US";

  const [data, setData] = useState<MonthPaymentDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!user?.userId || !year || !month) return;

      setData(null);
      setError(null);

      try {
        const endpoint = import.meta.env.VITE_GET_EDUCATOR_MONTH_EARNINGS
          .replace("{educator_id}", user.userId)
          .replace("{year}", year)
          .replace("{month}", month);
        const response = await api.get(endpoint);
        setData(response.data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError(t("unauthorized_access_error"));
        } else if (status === 404) {
          setError(t("educator_no_earnings", "No earnings data found for this month."));
        } else {
          setError(t("generic_error", "Something went wrong. Please try again later."));
        }
      }
    };

    fetchDetails();
  }, [user?.userId, year, month]);

  const handleDownloadPdf = async () => {
    if (!user?.userId || !year || !month) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const endpoint = import.meta.env.VITE_GET_EDUCATOR_EARNINGS_PDF
        .replace("{educator_id}", user.userId)
        .replace("{year}", year)
        .replace("{month}", month);
      const response = await api.get(`${endpoint}?download-language=${lang}`);
      window.open(response.data.PDF_URL, "_blank", "noopener,noreferrer");
    } catch {
      setPdfError(t("pdf_download_error", "Failed to generate PDF. Please try again."));
    } finally {
      setPdfLoading(false);
    }
  };

  const monthName = (() => {
    if (!year || !month) return "";
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString(locale, { month: "long" });
  })();

  const handleBack = () => {
    navigate(`/${language}/admin/earnings`);
  };

  const columns: AdminTableColumn<MonthPaymentDetail>[] = [
    {
      key: "date",
      header: t("payment_date_label"),
      cellDir: "auto",
      render: (item) => formatPaymentDate(item.created_at, locale),
    },
    {
      key: "class",
      header: t("class_title_label"),
      cellDir: "auto",
      render: (item) => item.class_title ?? DASH,
    },
    {
      key: "grade",
      header: t("grade_label"),
      align: "center",
      cellDir: "auto",
      render: (item) =>
        item.grade_localization_key
          ? t(item.grade_localization_key, item.grade_title ?? DASH)
          : DASH,
    },
    {
      key: "schedule",
      header: t("class_schedule_label"),
      align: "center",
      cellDir: "auto",
      render: (item) =>
        item.schedule_dates && item.schedule_dates.length > 0
          ? deriveScheduleString(item.schedule_dates, locale)
          : DASH,
    },
    {
      key: "session",
      header: t("session_title_label"),
      align: "center",
      cellDir: "auto",
      render: (item) => item.session_title ?? DASH,
    },
    {
      key: "student",
      header: t("student_name_label"),
      align: "center",
      cellDir: "auto",
      render: (item) => item.student_full_name ?? DASH,
    },
    {
      key: "payment",
      header: t("payment_amount_label"),
      cellDir: "ltr",
      render: (item) =>
        item.payment_amount != null
          ? parseFloat(String(item.payment_amount)).toFixed(2)
          : DASH,
    },
    {
      key: "remainder",
      header: t("payout_remainder_label"),
      cellDir: "ltr",
      render: (item) =>
        item.minimum_payout_remainder != null
          ? parseFloat(String(item.minimum_payout_remainder)).toFixed(2)
          : DASH,
    },
    {
      key: "net",
      header: t("net_payment_amount_label"),
      cellDir: "ltr",
      render: (item) => parseFloat(String(item.net_amount)).toFixed(2),
    },
    {
      key: "type",
      header: t("payment_type_label"),
      cellDir: "auto",
      render: (item) => t(item.payment_type, formatPaymentType(item.payment_type)),
    },
  ];

  if (error) {
    return (
      <div className="month-earnings-page__error">
        <p dir="auto">{error}</p>
      </div>
    );
  }

  if (data === null) {
    return null;
  }

  return (
    <main className="month-earnings-page">
      <div className="container">
        {/* ── Header row ── */}
        <div className="month-earnings-page__header">
          <button
            className="month-earnings-page__back-btn"
            onClick={handleBack}
            aria-label={t("back", "Back")}
          >
            <i className="fa-regular fa-arrow-left" aria-hidden="true" />
          </button>
          <h1 className="month-earnings-page__title" dir="auto">
            {monthName}&apos;s {t("earnings_label")}
          </h1>
          <div className="month-earnings-page__pdf-wrapper">
            <button
              className="month-earnings-page__pdf-btn"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
            >
              <i className="fa-regular fa-arrow-down-to-line" aria-hidden="true" />
              <span>
                {pdfLoading
                  ? t("downloading", "Downloading…")
                  : t("download_monthly_earnings_report")}
              </span>
            </button>
            {pdfError && (
              <p className="month-earnings-page__pdf-error" dir="auto">
                {pdfError}
              </p>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        {data.length === 0 ? (
          <div className="month-earnings-page__empty" dir="auto">
            {t("educator_no_earnings", "No payment records found for this month.")}
          </div>
        ) : (
          <AdminTable
            containerClassName="month-earnings-table"
            data={data}
            columns={columns}
            rowKey={(_, idx) => idx}
          />
        )}
      </div>
    </main>
  );
};

export default MonthEarnings;
